<script setup>
import { onMounted, computed } from 'vue'
import { useDataSourcesStore } from '@/stores/dataSources'

const dataSourcesStore = useDataSourcesStore()

onMounted(() => {
  dataSourcesStore.init()
})

const enrichments = computed(() => dataSourcesStore.enrichments)
const activeCount = computed(() => dataSourcesStore.activeEnrichments.length)
</script>

<template>
  <div class="manage-enrichments-container">
    <div class="enrichments-header">
      <h2 class="header-title">Enrichments</h2>
      <div class="header-subtitle">{{ activeCount }} active enrichments</div>
    </div>

    <div class="enrichments-list">
      <div class="enrichments-grid">
        <div
          v-for="e in enrichments"
          :key="e.label"
          class="enrichment-card"
          :class="{ disabled: !e.active }"
          :style="{ borderLeftColor: e.accent }"
        >
          <div class="card-icon" :style="{ color: e.accent }">
            <font-awesome-icon :icon="['fas', e.icon]" />
          </div>
          <div class="card-info">
            <span class="card-label">{{ e.label }}</span>
            <span class="card-desc">{{ e.desc }}</span>
          </div>
          <div class="card-meta">
            <div class="card-provider">
              <img v-if="e.providerLogo" src="~@/assets/ws_logo_idea.svg" class="provider-logo" height="12" />
              <span class="provider-name">{{ e.provider }}</span>
            </div>
            <span v-if="e.active" class="card-status active">Active</span>
            <span v-else class="card-status coming-soon">Coming soon</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.manage-enrichments-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  width: 100%;
}

.enrichments-header {
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f3f4f6;
  margin: 0 0 0.5rem 0;
}

.header-subtitle {
  font-size: 0.875rem;
  color: #9ca3af;
  font-weight: 500;
}

.enrichments-list {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

.enrichments-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.enrichment-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: rgba(26, 29, 36, 0.6);
  border-radius: 0.4rem;
  border-left: 2px solid;
  color: #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.enrichment-card.disabled {
  opacity: 0.35;
  border-left-style: dashed;
}

.card-icon {
  font-size: 1.1rem;
  width: 1.5rem;
  text-align: center;
  flex-shrink: 0;
}

.card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.card-label {
  font-weight: 500;
  font-size: 0.95rem;
  white-space: nowrap;
  line-height: 1;
}

.card-desc {
  font-size: 0.8rem;
  color: #7A8696;
  white-space: nowrap;
  line-height: 1;
}

.card-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
}

.card-provider {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.provider-logo {
  flex-shrink: 0;
  opacity: 0.7;
}

.provider-name {
  font-size: 0.7rem;
  color: #556172;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 500;
}

.card-status {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  white-space: nowrap;
}

.card-status.active {
  background-color: rgba(52, 211, 153, 0.12);
  color: #34d399;
}

.card-status.coming-soon {
  background-color: rgba(255, 255, 255, 0.05);
  color: #6B7280;
}

/* Scrollbar */
.enrichments-list::-webkit-scrollbar {
  width: 8px;
}

.enrichments-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.enrichments-list::-webkit-scrollbar-thumb {
  background: rgba(168, 85, 247, 0.3);
  border-radius: 4px;
}

.enrichments-list::-webkit-scrollbar-thumb:hover {
  background: rgba(168, 85, 247, 0.5);
}
</style>
